import React from "react";
import "./styles.css";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  IconButton,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import RestoreFromTrashIcon from "@material-ui/icons/RestoreFromTrash";

const baseUrl = "https://api.appetito.ro/";

function emptyUploadForm() {
  return {cityId: "0", position: "", restaurantId: "0", startDate: "", endDate: "", file: null, preview: null};
}

function emptyEditForm() {
  return {key: null, cityId: "0", position: "", restaurantId: "0", startDate: "", endDate: ""};
}

// Start/end dates are optional, both days included: no start = shown until
// the end date, no end = shown forever from the start, neither = always shown.
function datesError(form) {
  if (form.startDate && form.endDate && form.endDate < form.startDate) {
    return "Data de sfârșit trebuie să fie după data de început.";
  }
  return null;
}

function today() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function formatDate(date) {
  const [year, month, day] = date.split("-");
  return `${day}.${month}.${year}`;
}

// Label for the card: when the banner shows, and whether it is showing now.
function periodLabel(image) {
  const {start_date: start, end_date: end} = image;
  if (!start && !end) {
    return null;
  }
  const period = start && end
    ? `${formatDate(start)} – ${formatDate(end)}`
    : start ? `Din ${formatDate(start)}` : `Până la ${formatDate(end)}`;
  const now = today();
  if (start && now < start) {
    return {text: `${period} · Programat`, status: "scheduled"};
  }
  if (end && now > end) {
    return {text: `${period} · Expirat`, status: "expired"};
  }
  return {text: period, status: "active"};
}

export default class Bannere extends React.Component {
  state = {
    images: [],
    loading: true,
    cities: [],
    restaurants: [],
    showDeleted: false,
    dialogOpen: false,
    uploadForm: emptyUploadForm(),
    uploading: false,
    editDialogOpen: false,
    editForm: emptyEditForm(),
    editSaving: false,
  };

  componentDidMount() {
    this.loadImages();
    this.loadCities();
    this.loadRestaurants();
  }

  authHeaders = () => {
    const token = localStorage.getItem("access_token");
    return {Authorization: `Bearer ${token}`};
  };

  loadImages = async () => {
    this.setState({loading: true});
    try {
      const response = await axios.get(`${baseUrl}v1/admin/advertise-images`, {headers: this.authHeaders()});
      this.setState({images: response.data || []});
    } catch (e) {
      alert("Eroare la încărcarea bannerelor.");
    } finally {
      this.setState({loading: false});
    }
  };

  loadCities = async () => {
    const response = await axios.get(`${baseUrl}v1/admin/cities`, {headers: this.authHeaders()});
    this.setState({cities: response.data || []});
  };

  loadRestaurants = async () => {
    const response = await axios.get(`${baseUrl}v1/admin/restaurants`, {headers: this.authHeaders()});
    this.setState({restaurants: response.data || []});
  };

  cityName = (cityId) => {
    if (!cityId) {
      return "Toate orașele";
    }
    const city = this.state.cities.find((c) => c.id === cityId);
    return city ? city.name : `Oraș #${cityId}`;
  };

  restaurantName = (restaurantId) => {
    if (!restaurantId) {
      return null;
    }
    const restaurant = this.state.restaurants.find((r) => r.id === restaurantId);
    return restaurant ? restaurant.name : `Restaurant #${restaurantId}`;
  };

  openDialog = () => {
    this.setState({dialogOpen: true, uploadForm: emptyUploadForm()});
  };

  closeDialog = () => {
    this.setState({dialogOpen: false});
  };

  updateUploadField = (field) => (event) => {
    this.setState({uploadForm: {...this.state.uploadForm, [field]: event.target.value}});
  };

  onFileSelected = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    this.setState({
      uploadForm: {...this.state.uploadForm, file, preview: URL.createObjectURL(file)},
    });
  };

  saveBanner = async () => {
    const {uploadForm} = this.state;
    const position = parseInt(uploadForm.position, 10);
    if (!uploadForm.file) {
      alert("Alege o imagine.");
      return;
    }
    if (!position || position <= 0) {
      alert("Completează poziția.");
      return;
    }
    if (datesError(uploadForm)) {
      alert(datesError(uploadForm));
      return;
    }

    this.setState({uploading: true});
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(uploadForm.file);
      });
      const extension = (uploadForm.file.name.split(".").pop() || "jpg").toLowerCase();

      await axios.post(`${baseUrl}v1/admin/advertise-images`, {
        image: base64,
        extension,
        city_id: parseInt(uploadForm.cityId, 10) || 0,
        position,
        restaurant_id: parseInt(uploadForm.restaurantId, 10) || 0,
        start_date: uploadForm.startDate,
        end_date: uploadForm.endDate,
      }, {headers: this.authHeaders()});

      await this.loadImages();
      this.closeDialog();
    } catch (e) {
      alert("Eroare la încărcarea bannerului.");
    } finally {
      this.setState({uploading: false});
    }
  };

  openEditDialog = (image) => {
    this.setState({
      editDialogOpen: true,
      editForm: {
        key: image.key,
        cityId: String(image.city_id || 0),
        position: String(image.position || ""),
        restaurantId: String(image.restaurant_id || 0),
        startDate: image.start_date || "",
        endDate: image.end_date || "",
      },
    });
  };

  closeEditDialog = () => {
    this.setState({editDialogOpen: false});
  };

  updateEditField = (field) => (event) => {
    this.setState({editForm: {...this.state.editForm, [field]: event.target.value}});
  };

  saveEdit = async () => {
    const {editForm} = this.state;
    const position = parseInt(editForm.position, 10);
    if (!position || position <= 0) {
      alert("Completează poziția.");
      return;
    }
    if (datesError(editForm)) {
      alert(datesError(editForm));
      return;
    }

    this.setState({editSaving: true});
    try {
      await axios.put(`${baseUrl}v1/admin/advertise-images`, {
        key: editForm.key,
        city_id: parseInt(editForm.cityId, 10) || 0,
        position,
        restaurant_id: parseInt(editForm.restaurantId, 10) || 0,
        start_date: editForm.startDate,
        end_date: editForm.endDate,
      }, {headers: this.authHeaders()});

      await this.loadImages();
      this.closeEditDialog();
    } catch (e) {
      alert("Eroare la salvarea bannerului.");
    } finally {
      this.setState({editSaving: false});
    }
  };

  deleteBanner = async (image) => {
    if (!window.confirm("Ștergi acest banner?")) {
      return;
    }
    try {
      await axios.post(`${baseUrl}v1/admin/advertise-images/delete`, {key: image.key}, {headers: this.authHeaders()});
      await this.loadImages();
    } catch (e) {
      alert("Eroare la ștergerea bannerului.");
    }
  };

  recoverBanner = async (image) => {
    try {
      await axios.post(`${baseUrl}v1/admin/advertise-images/recover`, {key: image.key}, {headers: this.authHeaders()});
      await this.loadImages();
    } catch (e) {
      alert("Eroare la recuperarea bannerului.");
    }
  };

  render() {
    const {
      images, loading, cities, restaurants, showDeleted,
      dialogOpen, uploadForm, uploading,
      editDialogOpen, editForm, editSaving,
    } = this.state;
    const visibleImages = showDeleted ? images : images.filter((image) => !image.deleted);

    return (
      <div className="settings-container">
        <div className="settings-header-row">
          <h2 className="settings-title" style={{marginBottom: 0}}>Bannere</h2>
          <div className="settings-header-row-actions">
            <FormControlLabel
              control={
                <Switch
                  checked={showDeleted}
                  onChange={(event) => this.setState({showDeleted: event.target.checked})}
                />
              }
              label="Arată bannerele șterse"
            />
            <Button variant="contained" color="primary" startIcon={<AddIcon/>} onClick={this.openDialog}>
              Adaugă Banner
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loader"/>
          </div>
        ) : (
          <div className="banners-grid">
            {visibleImages.length === 0 && (
              <div className="settings-description">Niciun banner încărcat.</div>
            )}
            {visibleImages.map((image, i) => {
              const restaurantLabel = this.restaurantName(image.restaurant_id);
              const period = periodLabel(image);
              return (
                <div className={`banner-card${image.deleted ? ' banner-card-deleted' : ''}`} key={`${image.key}-${i}`}>
                  <img src={image.url} alt="banner" className="banner-card-image"/>
                  <div className="banner-card-info">
                    <div className="banner-card-details">
                      <span className="banner-card-city">{this.cityName(image.city_id)}</span>
                      <span className="banner-card-meta">Poziție {image.position}</span>
                      {restaurantLabel && <span className="banner-card-meta">{restaurantLabel}</span>}
                      {period && (
                        <span className={`banner-card-meta banner-card-period banner-card-period-${period.status}`}>
                          {period.text}
                        </span>
                      )}
                    </div>
                    <div className="banner-card-actions">
                      {image.deleted ? (
                        <IconButton size="small" title="Recuperează" onClick={() => this.recoverBanner(image)}>
                          <RestoreFromTrashIcon fontSize="small"/>
                        </IconButton>
                      ) : (
                        <>
                          <IconButton size="small" title="Editează" onClick={() => this.openEditDialog(image)}>
                            <EditIcon fontSize="small"/>
                          </IconButton>
                          <IconButton size="small" title="Șterge" onClick={() => this.deleteBanner(image)}>
                            <DeleteIcon fontSize="small"/>
                          </IconButton>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Dialog open={dialogOpen} onClose={this.closeDialog} fullWidth maxWidth="xs">
          <DialogTitle>Adaugă Banner</DialogTitle>
          <DialogContent>
            <label className="banner-upload-button">
              <input type="file" accept="image/*" onChange={this.onFileSelected}/>
              {uploadForm.file ? "Schimbă imaginea" : "Alege imagine"}
            </label>
            {uploadForm.preview && (
              <img src={uploadForm.preview} alt="preview" style={{width: "100%", marginTop: 12, borderRadius: 8}}/>
            )}

            <TextField
              select
              label="Oraș"
              fullWidth
              margin="normal"
              value={uploadForm.cityId}
              onChange={this.updateUploadField("cityId")}
            >
              <MenuItem value="0">Toate orașele</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.id} value={String(city.id)}>{city.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Poziție"
              type="number"
              fullWidth
              margin="normal"
              value={uploadForm.position}
              onChange={this.updateUploadField("position")}
            />

            <TextField
              select
              label="Restaurant (opțional)"
              fullWidth
              margin="normal"
              value={uploadForm.restaurantId}
              onChange={this.updateUploadField("restaurantId")}
            >
              <MenuItem value="0">Fără restaurant</MenuItem>
              {restaurants.map((restaurant) => (
                <MenuItem key={restaurant.id} value={String(restaurant.id)}>
                  {restaurant.name} {restaurant.city}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Activ de la (opțional)"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{shrink: true}}
              value={uploadForm.startDate}
              onChange={this.updateUploadField("startDate")}
            />

            <TextField
              label="Activ până la (opțional)"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{shrink: true}}
              value={uploadForm.endDate}
              onChange={this.updateUploadField("endDate")}
              helperText="Fără date, bannerul e activ mereu. Ambele zile sunt incluse."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog} disabled={uploading}>
              Anulează
            </Button>
            <Button onClick={this.saveBanner} color="primary" variant="contained" disabled={uploading}>
              Salvează
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editDialogOpen} onClose={this.closeEditDialog} fullWidth maxWidth="xs">
          <DialogTitle>Editează Banner</DialogTitle>
          <DialogContent>
            <TextField
              select
              label="Oraș"
              fullWidth
              margin="normal"
              value={editForm.cityId}
              onChange={this.updateEditField("cityId")}
            >
              <MenuItem value="0">Toate orașele</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.id} value={String(city.id)}>{city.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Poziție"
              type="number"
              fullWidth
              margin="normal"
              value={editForm.position}
              onChange={this.updateEditField("position")}
            />

            <TextField
              select
              label="Restaurant (opțional)"
              fullWidth
              margin="normal"
              value={editForm.restaurantId}
              onChange={this.updateEditField("restaurantId")}
            >
              <MenuItem value="0">Fără restaurant</MenuItem>
              {restaurants.map((restaurant) => (
                <MenuItem key={restaurant.id} value={String(restaurant.id)}>
                  {restaurant.name} {restaurant.city}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Activ de la (opțional)"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{shrink: true}}
              value={editForm.startDate}
              onChange={this.updateEditField("startDate")}
            />

            <TextField
              label="Activ până la (opțional)"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{shrink: true}}
              value={editForm.endDate}
              onChange={this.updateEditField("endDate")}
              helperText="Fără date, bannerul e activ mereu. Ambele zile sunt incluse."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeEditDialog} disabled={editSaving}>
              Anulează
            </Button>
            <Button onClick={this.saveEdit} color="primary" variant="contained" disabled={editSaving}>
              Salvează
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
